import { createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit';
import { applyBufferData } from '../../actions/buffer';
import { resetOrder } from '../../actions/currentOrder';
import {
  adjustOpenPurchaseChoice,
  adjustOpenPurchaseSubstitution,
  setOpenPurchaseChoiceSelections,
  setOpenPurchaseSubstitutionSelections,
  changeOpenPurchaseItem,
  openPurchase,
  openStagedPurchase,
  resetOpenPurchaseChoiceSets,
  stageOpenPurchase,
  updateMiamSelections,
  removeMiamSelections,
} from '../../operations';

const initialState = null as _Purchase | null;

interface MiamSelectionDto {
  readonly key: string;
  readonly selectedChoiceId?: string;
}

const slice = createSlice({
  name: '$openPurchase',
  initialState,
  reducers: {
    setOpenPurchase(state, action: PayloadAction<_Purchase>) {
      return action.payload || initialState;
    },
    adjustOpenPurchaseQuantity(state, action: PayloadAction<number>) {
      if (state) {
        return {
          ...state,
          quantity: state.quantity + action.payload,
        };
      } else {
        return initialState;
      }
    },
    adjustOpenPurchaseMiamQuantity(
      state,
      action: PayloadAction<{ miamId: string; quantity: number }>,
    ) {
      if (state) {
        let { miamQuantityMap } = state;
        const { miamId, quantity } = action.payload;
        if (miamQuantityMap) {
          return {
            ...state,
            miamQuantityMap: {
              ...miamQuantityMap,
              [miamId]:
                miamId in miamQuantityMap
                  ? miamQuantityMap[miamId] + quantity
                  : quantity,
            },
          };
        } else {
          return { ...state, miamQuantityMap: { [miamId]: quantity } };
        }
      } else {
        return initialState;
      }
    },
    storeOpenPurchaseMiamSelection(
      state,
      action: PayloadAction<MiamSelectionDto>,
    ) {
      if (state) {
        const { miamQuantityMap, choiceSelections } = state;
        const {key, selectedChoiceId} = action.payload
        
        if (choiceSelections) {
          const miamSelections = choiceSelections[key] as SDict<
            SDict<ChoiceWithQuantity[]>
          >;
          const quantity = (miamQuantityMap || {})[key] || 1; 
          const selections = {[key]: miamSelections};
          return {
            ...state,
            miam: {
              [key]: {
                quantity,
                selections,
                selectedChoiceId,
              },
            },
          };
        }
      }
      return initialState;
    },
    clearOpenPurchase() {
      return initialState;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(resetOrder, () => {
        return initialState;
      })
      .addCase(applyBufferData, (state, action) => {
        const { openPurchase } = action.payload;
        return openPurchase === undefined ? state : openPurchase;
      })
      .addMatcher(
        isAnyOf(
          changeOpenPurchaseItem.fulfilled,
          adjustOpenPurchaseChoice.fulfilled,
          adjustOpenPurchaseSubstitution.fulfilled,
          resetOpenPurchaseChoiceSets.fulfilled,
          openPurchase.fulfilled,
          openStagedPurchase.fulfilled,
          // olo3:
          setOpenPurchaseChoiceSelections.fulfilled,
          setOpenPurchaseSubstitutionSelections.fulfilled,
          updateMiamSelections.fulfilled,
          removeMiamSelections.fulfilled,
        ),
        (state, action) => {
          return action.payload?.purchase || initialState;
        },
      )
      .addMatcher(isAnyOf(stageOpenPurchase.fulfilled), () => initialState);
  },
});

export const {
  setOpenPurchase,
  adjustOpenPurchaseQuantity,
  adjustOpenPurchaseMiamQuantity,
  storeOpenPurchaseMiamSelection,
  clearOpenPurchase,
} = slice.actions;
export default slice.reducer;
